import { autoUpdate, flip, offset, size, useFloating } from "@floating-ui/react";
import clsx from "clsx";
import { useEffect, useMemo, useRef, useState } from "react";

import { Icon, Portal } from "@/components/common";
import { InputError, InputValue } from "@/components/common/inputs";
import { useInput } from "@/hooks/common/use-input";
import { DefaultComponentProps } from "@/interfaces/common";
import { FormControl } from "@/interfaces/form";

import { InputDropdownItem } from "./InputDropdownItem";

export interface InputDropdownOptionProps {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface InputDropdownProps extends DefaultComponentProps {
  label?: string;
  disabled?: boolean;
  control?: FormControl;
  value?: string | null;
  onChange?: (value: string | null) => void;
  options: InputDropdownOptionProps[];
  field: string;
  type?: "default" | "search" | "select";
  isMultiSelect?: boolean;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  placeholderClassName?: string;
  noOptionSelectedText?: string;
  noOptionSelectedTextClassName?: string;
  iconLeft?: React.ReactNode;
  usePortalMenu?: boolean;
  helperText?: string;
}

const IconArrow = ({ isOpen }: { isOpen: boolean }) => (
  <Icon
    name="KeyboardArrowDown"
    className={clsx("text-primary pr-2 transition-transform duration-200", { "rotate-180": isOpen })}
  />
);

const InputDropdownFormField = ({ control, field }: { control: FormControl; field: string }) => {
  const { registration } = useInput({ control, field, shouldRegister: true });

  return <input {...registration} id={field} className="hidden" />;
};

const InputDropdownFormError = ({ control, field }: { control: FormControl; field: string }) => {
  const { errorMessage } = useInput({ control, field, shouldRegister: false });

  return errorMessage ? <InputError message={errorMessage} className="mt-0" /> : null;
};

export const InputDropdown = ({
  label = "",
  control,
  value: controlledValue,
  onChange,
  options,
  field,
  type = "default",
  isMultiSelect = false,
  placeholder = "",
  disabled,
  className,
  buttonClassName,
  menuClassName,
  placeholderClassName,
  noOptionSelectedText = "Selecione uma opção",
  noOptionSelectedTextClassName,
  iconLeft,
  usePortalMenu = false,
  helperText,
}: InputDropdownProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropDirection, setDropDirection] = useState("down");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLFieldSetElement | null>(null);
  const isSearchable = type === "search";
  const value = control ? control.watch(field) : controlledValue;
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
    placement: "bottom-start",
    middleware: [
      offset(4),
      flip(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, { minWidth: `${rects.reference.width}px` });
        },
      }),
    ],
  });

  const valuesSelected = isMultiSelect ? (Array.isArray(value) ? value : []) : [];
  const textValue = useMemo(() => {
    if (isMultiSelect) {
      if (valuesSelected.length === 0) return noOptionSelectedText;
      if (valuesSelected.length === 1) return "1 opção selecionada";
      return `${valuesSelected.length} opções selecionadas`;
    }

    return options.find((option) => option.value === value)?.label || noOptionSelectedText;
  }, [isMultiSelect, noOptionSelectedText, options, value, valuesSelected.length]);

  const filteredOptions = isSearchable
    ? options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const selectItem = (item: string) => {
    const nextValue = isMultiSelect
      ? valuesSelected.includes(item)
        ? valuesSelected.filter((selectedItem) => selectedItem !== item)
        : [...valuesSelected, item]
      : value === item
        ? null
        : item;

    if (control) {
      control.onChange({ target: { name: field, value: nextValue } });
    }
    onChange?.(nextValue as string | null);

    if (!isMultiSelect) {
      if (nextValue !== null) setSearchTerm(item);
      setIsOpen(false);
    }
  };

  const menu = isOpen ? (
    <div
      ref={(element) => {
        dropdownRef.current = element;
        if (usePortalMenu) refs.setFloating(element);
      }}
      style={usePortalMenu ? floatingStyles : undefined}
      className={clsx(
        "mini-scrollbar border-1 border-input-border flex flex-col gap-2",
        "z-50 max-h-52 overflow-y-auto rounded-none border bg-white p-4 shadow-md",
        usePortalMenu ? "w-max" : "absolute left-0 right-0 w-full",
        !usePortalMenu && { "bottom-full mb-1": dropDirection === "up", "mt-1": dropDirection === "down" },
        menuClassName,
      )}
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => (
          <InputDropdownItem
            key={index}
            variant={isMultiSelect ? "check" : "radio"}
            option={option}
            onClick={selectItem}
            field={field}
            disabled={option.disabled}
            isSelected={isMultiSelect ? valuesSelected.includes(option.value) : value === option.value}
          />
        ))
      ) : (
        <div className="flex w-full items-center justify-center">
          <span className="text-sm">Nenhuma opção encontrada</span>
        </div>
      )}
    </div>
  ) : null;

  useEffect(() => {
    if (isOpen && dropdownRef.current && buttonRef.current && !usePortalMenu) {
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const buttonBottom = buttonRef.current.getBoundingClientRect().bottom;
      setDropDirection(window.innerHeight - buttonBottom < dropdownHeight + 10 ? "up" : "down");
    }
  }, [isOpen, filteredOptions.length, usePortalMenu]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
      setIsFocused(false);
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <fieldset
      className={clsx("relative", className)}
      ref={(element) => {
        buttonRef.current = element;
        if (usePortalMenu) refs.setReference(element);
      }}
      aria-label={field}
    >
      <div className="w-full">
        {label && (
          <label className={clsx("label", { "opacity-50": disabled })} htmlFor={field}>
            {label}
          </label>
        )}
        {isSearchable && control && (
          <div onClick={disabled ? undefined : () => setIsOpen(true)} onFocus={() => setIsFocused(true)}>
            <InputValue
              field={field}
              control={control}
              onChange={({ target }) => setSearchTerm(target.value)}
              placeholder={isFocused ? placeholder : noOptionSelectedText}
              value={searchTerm}
              className="transform transition-all duration-300 ease-in-out"
              iconLeft={
                isFocused
                  ? () => (
                      <Icon
                        name="Search"
                        className="transform pl-4 text-primary transition-all duration-300 ease-in-out"
                        size="1.25rem"
                      />
                    )
                  : undefined
              }
              iconRight={() => <IconArrow isOpen={isOpen} />}
            />
          </div>
        )}
        {!isSearchable && (
          <button
            onClick={() => setIsOpen((previousValue) => !previousValue)}
            type="button"
            className={clsx(
              "text-placeholder flex h-10 w-full items-center gap-3 rounded-none bg-white px-4 py-2 text-sm font-medium transition-colors",
              { "opacity-50": disabled, "shadow-[inset_0_-2px] shadow-input-border hover:shadow-primary": !disabled },
              buttonClassName,
            )}
            disabled={disabled}
          >
            {iconLeft && <span className="flex-shrink-0">{iconLeft}</span>}
            <span
              className={clsx(
                "flex-1 text-start text-sm font-normal",
                placeholderClassName,
                textValue === noOptionSelectedText && (noOptionSelectedTextClassName ?? "text-placeholder opacity-60"),
              )}
            >
              {textValue}
            </span>
            {control && <InputDropdownFormField control={control} field={field} />}
            <IconArrow isOpen={isOpen} />
          </button>
        )}
      </div>
      {usePortalMenu ? <Portal>{menu}</Portal> : menu}
      {control && !isSearchable && <InputDropdownFormError control={control} field={field} />}
      {helperText && <span className="text-sm block mt-1">{helperText}</span>}
    </fieldset>
  );
};
